
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Users, 
  Search, 
  Plus, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  GraduationCap,
  Award,
  MapPin
} from 'lucide-react';

const HQStaff = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const staffMembers = [
    {
      id: 1,
      name: "Dr. Sarah Johnson",
      role: "Medical Director",
      location: "Austin Studio",
      status: "active",
      trainingProgress: 100,
      certifications: ["Botox", "Dermal Fillers", "Medical Oversight"],
      joinDate: "2023-01-15",
      lastActive: "2 hours ago"
    },
    {
      id: 2,
      name: "Emily Rodriguez",
      role: "Nurse Injector",
      location: "Dallas Downtown",
      status: "active", 
      trainingProgress: 85,
      certifications: ["Botox", "Basic Dermal Fillers"],
      joinDate: "2023-06-20",
      lastActive: "1 day ago"
    },
    {
      id: 3,
      name: "Michael Chen",
      role: "Patient Coordinator",
      location: "Houston Medical",
      status: "training",
      trainingProgress: 60,
      certifications: ["Customer Service", "Booking Systems"],
      joinDate: "2024-01-10",
      lastActive: "3 hours ago"
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-emerald-500/20 text-emerald-200 border-emerald-500/30';
      case 'training': return 'bg-amber-500/20 text-amber-200 border-amber-500/30';
      case 'inactive': return 'bg-slate-500/20 text-slate-200 border-slate-500/30';
      default: return 'bg-slate-500/20 text-slate-200 border-slate-500/30';
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 90) return 'bg-emerald-600';
    if (progress >= 70) return 'bg-amber-600';
    return 'bg-red-600';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Staff Management
              </h1>
              <p className="mt-1 text-amber-200">
                Track team onboarding, access, and training progress
              </p>
            </div>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Add Team Member
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Search and Filters */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search staff members..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                  />
                </div>
                <div className="flex space-x-3">
                  <Button variant="outline" className="border-slate-600 text-slate-300">
                    Filter by Location
                  </Button>
                  <Button variant="outline" className="border-slate-600 text-slate-300">
                    Filter by Role
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Staff Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-slate-800/50 backdrop-blur border-emerald-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-emerald-500/20">
                    <Users className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-200">Active Staff</p>
                    <p className="text-2xl font-bold text-white">24</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-amber-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-amber-500/20">
                    <GraduationCap className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-200">In Training</p>
                    <p className="text-2xl font-bold text-white">5</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-blue-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-blue-500/20">
                    <Award className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-200">Certified</p>
                    <p className="text-2xl font-bold text-white">19</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Staff List */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardHeader>
              <CardTitle className="text-white">Team Directory</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {staffMembers.map((member) => (
                  <div key={member.id} className="p-4 bg-slate-700/30 rounded-lg border border-slate-600/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold">
                            {member.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">{member.name}</h3>
                          <p className="text-slate-300 text-sm">{member.role}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <MapPin className="w-3 h-3 text-slate-400" />
                            <span className="text-slate-400 text-xs">{member.location}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="text-slate-300 text-sm">Training Progress</p>
                          <div className="flex items-center space-x-2">
                            <div className="w-20 h-2 bg-slate-600 rounded-full">
                              <div 
                                className={`h-2 rounded-full ${getProgressColor(member.trainingProgress)}`}
                                style={{ width: `${member.trainingProgress}%` }}
                              ></div>
                            </div>
                            <span className="text-white text-sm">{member.trainingProgress}%</span>
                          </div>
                        </div>
                        
                        <Badge className={getStatusColor(member.status)}>
                          {member.status === 'active' && <CheckCircle className="w-3 h-3 mr-1" />}
                          {member.status === 'training' && <Clock className="w-3 h-3 mr-1" />}
                          {member.status === 'inactive' && <AlertCircle className="w-3 h-3 mr-1" />}
                          {member.status}
                        </Badge>
                        
                        <Button size="sm" variant="outline" className="border-slate-600 text-slate-300">
                          Manage
                        </Button>
                      </div>
                    </div>
                    
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex flex-wrap gap-1">
                        {member.certifications.map((cert, index) => (
                          <Badge key={index} className="bg-blue-500/20 text-blue-200 border-blue-500/30 text-xs">
                            {cert}
                          </Badge>
                        ))}
                      </div>
                      <p className="text-slate-400 text-xs">Last active: {member.lastActive}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HQStaff;
